import React, { useState, useEffect } from "react";
import { useAuth, API, formatApiErrorDetail } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import {
  format,
  startOfMonth,
  endOfMonth,
  parseISO,
  isSameMonth,
  addMonths,
  subMonths,
} from "date-fns";
import { ChevronLeft, ChevronRight, Clock, MapPin, ArrowRightLeft, X, MessageSquare, Loader2 } from "lucide-react";

const WorkSchedule = () => {
  const { user, getAuthHeaders } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedShift, setSelectedShift] = useState(null);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [showDropDialog, setShowDropDialog] = useState(false);
  const [dropReason, setDropReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [shiftNotes, setShiftNotes] = useState([]);

  useEffect(() => {
    fetchShifts();
  }, [currentDate]);

  const fetchShifts = async () => {
    setLoading(true);
    try {
      const start = format(startOfMonth(currentDate), "yyyy-MM-dd");
      const end = format(endOfMonth(currentDate), "yyyy-MM-dd");
      const response = await axios.get(
        `${API}/shifts/my?start_date=${start}&end_date=${end}`,
        { headers: getAuthHeaders() }
      );
      setShifts(response.data);
    } catch (error) {
      console.error("Error fetching shifts:", error);
    } finally {
      setLoading(false);
    }
  };

  const getShiftsForDate = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return shifts.filter((s) => s.date === dateStr);
  };

  const handleDateClick = (date) => {
    const dayShifts = getShiftsForDate(date);
    if (dayShifts.length > 0) {
      setSelectedShift(dayShifts[0]);
      setShowActionDialog(true);
      // Fetch notes for this shift
      axios.get(`${API}/shifts/${dayShifts[0].id}/notes`, { headers: getAuthHeaders() })
        .then(res => setShiftNotes(res.data))
        .catch(() => setShiftNotes([]));
    }
  };

  const handleTradeRequest = () => {
    toast.info("Trade functionality coming soon!");
    setShowActionDialog(false);
  };

  const handleDropRequest = async () => {
    if (!dropReason.trim()) {
      toast.error("Please provide a reason for the drop request");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/drop-requests`,
        { shift_id: selectedShift.id, reason: dropReason },
        { headers: getAuthHeaders() }
      );
      toast.success("Drop request submitted successfully!");
      setShowDropDialog(false);
      setShowActionDialog(false);
      setDropReason("");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to submit drop request");
    } finally {
      setSubmitting(false);
    }
  };

  const scheduledDates = shifts.map((s) => parseISO(s.date));

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="work-schedule-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading text-[#1A1A1A]">Work Schedule</h1>
          <p className="text-[#666666]">Your personal schedule for {format(currentDate, "MMMM yyyy")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCurrentDate(subMonths(currentDate, 1))}
            className="border-[#E5E5E5]"
            data-testid="prev-month"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm font-medium px-4">{format(currentDate, "MMMM yyyy")}</span>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCurrentDate(addMonths(currentDate, 1))}
            className="border-[#E5E5E5]"
            data-testid="next-month"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <Card className="border border-[#E5E5E5] shadow-none">
        <CardContent className="p-6">
          {loading ? (
            <div className="flex items-center justify-center h-96">
              <div className="animate-pulse text-[#666666]">Loading schedule...</div>
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-1">
              {/* Day Headers */}
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <div
                  key={day}
                  className="text-center text-xs font-semibold uppercase tracking-wider text-[#666666] py-2"
                >
                  {day}
                </div>
              ))}
              {/* Calendar Days */}
              {(() => {
                const start = startOfMonth(currentDate);
                const startDay = start.getDay();
                const daysInMonth = endOfMonth(currentDate).getDate();
                const cells = [];

                // Empty cells for days before month starts
                for (let i = 0; i < startDay; i++) {
                  cells.push(
                    <div key={`empty-${i}`} className="min-h-[100px] bg-[#FAFAFA] border border-[#E5E5E5]/50" />
                  );
                }

                // Days of the month
                for (let day = 1; day <= daysInMonth; day++) {
                  const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
                  const dayShifts = getShiftsForDate(date);
                  const isToday =
                    format(date, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");

                  cells.push(
                    <div
                      key={day}
                      className={`min-h-[100px] p-2 border border-[#E5E5E5] cursor-pointer transition-colors ${
                        dayShifts.length > 0
                          ? "bg-[#F0EAEB] hover:bg-[#E2D4D6]"
                          : "bg-white hover:bg-[#FAFAFA]"
                      } ${isToday ? "ring-2 ring-[#6A1E2E]" : ""}`}
                      onClick={() => handleDateClick(date)}
                      data-testid={`calendar-day-${day}`}
                    >
                      <div
                        className={`text-sm font-medium mb-1 ${
                          isToday ? "text-[#6A1E2E]" : "text-[#1A1A1A]"
                        }`}
                      >
                        {day}
                      </div>
                      {dayShifts.map((shift, idx) => (
                        <div
                          key={idx}
                          className="text-xs p-1 bg-[#6A1E2E] text-white rounded-sm mb-1"
                        >
                          <div className="font-medium truncate">{shift.event_name}</div>
                          <div className="military-time opacity-80">
                            {shift.start_time}-{shift.end_time}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                }

                return cells;
              })()}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Shift Details Legend */}
      <div className="flex items-center gap-6 text-sm text-[#666666]">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-[#F0EAEB] border border-[#6A1E2E] rounded-sm" />
          <span>Scheduled Day</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 ring-2 ring-[#6A1E2E] rounded-sm" />
          <span>Today</span>
        </div>
      </div>

      {/* Action Dialog */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Shift Options</DialogTitle>
            <DialogDescription>
              {selectedShift && (
                <div className="mt-3 p-3 bg-[#F0EAEB] rounded-sm">
                  <div className="flex items-center gap-2 text-[#1A1A1A] font-semibold">
                    <MapPin className="w-4 h-4 text-[#6A1E2E]" />
                    {selectedShift.event_name}
                  </div>
                  <div className="flex items-center gap-2 text-[#666666] text-sm mt-1">
                    <Clock className="w-4 h-4" />
                    <span className="military-time">
                      {selectedShift.start_time} - {selectedShift.end_time}
                    </span>
                  </div>
                  <div className="text-sm text-[#666666] mt-1">
                    {selectedShift.date && format(parseISO(selectedShift.date), "EEEE, MMMM d, yyyy")}
                  </div>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {/* Notes */}
            {shiftNotes.length > 0 && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-sm">
                <div className="text-xs font-semibold text-yellow-800 flex items-center gap-1 mb-1.5">
                  <MessageSquare className="w-3 h-3" /> Notes
                </div>
                {shiftNotes.map(note => (
                  <div key={note.id} className="text-sm text-yellow-900 mb-1">
                    <span>{note.text}</span>
                    <span className="text-[10px] text-yellow-600 ml-1">— {note.author_name}</span>
                  </div>
                ))}
              </div>
            )}
            <Button
              onClick={handleTradeRequest}
              className="w-full bg-[#6A1E2E] hover:bg-[#521422] text-white"
              data-testid="trade-tour-btn"
            >
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              Trade the Tour
            </Button>
            <Button
              onClick={() => {
                setShowDropDialog(true);
                setShowActionDialog(false);
              }}
              variant="outline"
              className="w-full border-[#E5E5E5]"
              data-testid="request-drop-btn"
            >
              <X className="w-4 h-4 mr-2" />
              Request a Tour Drop
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Drop Request Dialog */}
      <Dialog open={showDropDialog} onOpenChange={setShowDropDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Request Tour Drop</DialogTitle>
            <DialogDescription>
              Please provide a reason for requesting to drop this shift.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Textarea
              placeholder="Reason for drop request..."
              value={dropReason}
              onChange={(e) => setDropReason(e.target.value)}
              className="border-[#E5E5E5] min-h-[100px]"
              data-testid="drop-reason-input"
            />
          </div>
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setShowDropDialog(false)}
              className="border-[#E5E5E5]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDropRequest}
              disabled={submitting}
              className="bg-[#6A1E2E] hover:bg-[#521422] text-white"
              data-testid="submit-drop-request"
            >
              {submitting ? "Submitting..." : "Submit Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkSchedule;
